import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, BookOpen } from 'lucide-react';

const MagicLoader = ({
  size = 'medium',
  variant = 'bouncing',
  message = 'Creating magic...',
  className = ''
}) => {
  const sizeClasses = {
    small: 'w-6 h-6',
    medium: 'w-12 h-12',
    large: 'w-20 h-20'
  };

  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  };

  if (variant === 'bouncing') {
    return (
      <div className={`flex flex-col items-center justify-center gap-4 ${className}`}>
        <div className="relative">
          {/* Bouncing sparkles */}
          <motion.div
            animate={{
              y: [0, -20, 0],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className={`${sizeClasses[size]} flex items-center justify-center`}
          >
            <Sparkles className="w-full h-full text-yellow-400" />
          </motion.div>

          {/* Floating particles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-yellow-300 rounded-full"
              animate={{
                y: [0, -30, 0],
                x: [0, Math.sin(i * 60 * Math.PI / 180) * 20, 0],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.2,
                ease: "easeInOut"
              }}
              style={{
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            />
          ))}
        </div>

        {message && (
          <motion.p
            className={`${textSizeClasses[size]} text-gray-600 font-medium`}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            {message}
          </motion.p>
        )}
      </div>
    );
  }

  if (variant === 'turning-pages') {
    return (
      <div className={`flex flex-col items-center justify-center gap-4 ${className}`}>
        <div className="relative">
          {/* Book pages turning */}
          <motion.div
            className={`${sizeClasses[size]} relative`}
            style={{ perspective: '1000px' }}
          >
            <BookOpen className="w-full h-full text-blue-500 absolute" />

            {/* Animated page turns */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-blue-200 to-purple-200 rounded"
              animate={{ rotateY: [0, -180, 0] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{ transformOrigin: 'left center' }}
            />

            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-purple-200 to-pink-200 rounded"
              animate={{ rotateY: [0, 180, 0] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: 0.5,
                ease: "easeInOut"
              }}
              style={{ transformOrigin: 'right center' }}
            />
          </motion.div>

          {/* Sparkle effects */}
          <motion.div
            className="absolute -top-2 -right-2"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 1,
              repeat: Infinity
            }}
          >
            <Sparkles className="w-4 h-4 text-yellow-400" />
          </motion.div>
        </div>

        {message && (
          <motion.p
            className={`${textSizeClasses[size]} text-gray-600 font-medium`}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            {message}
          </motion.p>
        )}
      </div>
    );
  }

  // Default bouncing variant
  return (
    <div className={`flex flex-col items-center justify-center gap-4 ${className}`}>
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 360]
        }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className={`${sizeClasses[size]} bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg`}
      >
        <Sparkles className="w-1/2 h-1/2 text-white" />
      </motion.div>

      {message && (
        <motion.p
          className={`${textSizeClasses[size]} text-gray-600 font-medium`}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          {message}
        </motion.p>
      )}
    </div>
  );
};

export default MagicLoader;
